(() => {
  'use strict';
  if (window.BSProductInitialized) return;
  window.BSProductInitialized = true;
  const requests = new WeakMap();
  const modelPlayers = new WeakMap();
  const queryRoot = target => target.closest('[data-bs-product]');
  function pauseMedia(panel) {
    panel.querySelectorAll('video').forEach(video => video.pause());
    panel.querySelectorAll('model-viewer').forEach(model => modelPlayers.get(model)?.pause());
    panel.querySelectorAll('iframe').forEach(frame => {
      try {
        const host = new URL(frame.src).hostname;
        if (host.includes('youtube.com') || host.includes('youtube-nocookie.com')) frame.contentWindow?.postMessage(JSON.stringify({event: 'command', func: 'pauseVideo', args: []}), new URL(frame.src).origin);
        else if (host.includes('vimeo.com')) frame.contentWindow?.postMessage(JSON.stringify({method: 'pause'}), new URL(frame.src).origin);
      } catch {}
    });
  }
  function selectMedia(root, id) {
    const panels = [...root.querySelectorAll('[data-bs-media-panel]')];
    if (!panels.some(panel => panel.dataset.bsMediaPanel === id)) return;
    panels.forEach(panel => {
      const visible = panel.dataset.bsMediaPanel === id;
      if (!visible) pauseMedia(panel);
      panel.hidden = !visible;
    });
    root.querySelectorAll('[data-bs-media-target]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.bsMediaTarget === id)));
  }
  function loadMedia(button) {
    const panel = button.closest('[data-bs-media-panel]');
    const template = panel.querySelector('[data-bs-media-template]');
    if (!template) return;
    button.replaceWith(template.content.cloneNode(true));
    template.remove();
    const model = panel.querySelector('model-viewer');
    if (model && window.Shopify?.loadFeatures) {
      Shopify.loadFeatures([{name: 'model-viewer-ui', version: '1.0', onLoad: error => {
        if (error || !window.Shopify.ModelViewerUI || !model.isConnected) return;
        const player = new Shopify.ModelViewerUI(model);
        modelPlayers.set(model, player);
        model.focus();
      }}]);
    }
    const frame = panel.querySelector('iframe');
    if (frame) frame.focus();
  }
  function selectedURL(root, input) {
    const url = new URL(input.dataset.bsProductUrl || root.dataset.productUrl, location.origin);
    const current = new URL(location.href);
    if (url.pathname === current.pathname) url.search = current.search;
    else {
      for (const key of ['preview_theme_id', 'pb', '_fd']) if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    }
    url.searchParams.delete('variant');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    const values = [...root.querySelectorAll('[data-bs-option-value]:checked')].map(option => option.dataset.bsOptionValue);
    url.searchParams.set('option_values', values.join(','));
    return url;
  }
  function freezeBuy(root) {
    root.dataset.updating = 'true';
    root.setAttribute('aria-busy', 'true');
    root.querySelectorAll('[data-bs-product-form] button, [data-bs-product-form] input[name=id], [data-bs-product-form] input[name=quantity]').forEach(control => { control.disabled = true; });
    root.querySelectorAll('.bs-product-dynamic-checkout').forEach(wrapper => { wrapper.inert = true; });
  }
  function preserveQuantity(nextRoot, oldQuantity) {
    const field = nextRoot.querySelector('[data-bs-product-form] [name=quantity]');
    if (!field || !Number.isFinite(oldQuantity)) return;
    const min = Number(field.min) || 1, step = Number(field.step) || 1;
    const max = field.max ? Number(field.max) : Infinity;
    const bounded = Math.min(Math.max(oldQuantity, min), max);
    field.value = String(Math.max(min, min + Math.floor((bounded - min) / step) * step));
  }
  async function updateVariant(root, input) {
    const url = selectedURL(root, input);
    if (url.origin !== location.origin) return;
    if (url.pathname !== new URL(root.dataset.productUrl, location.origin).pathname) {
      location.assign(url.href);
      return;
    }
    requests.get(root)?.abort();
    const controller = new AbortController();
    requests.set(root, controller);
    const oldQuantity = Number(root.querySelector('[data-bs-product-form] [name=quantity]')?.value);
    const focusedId = input.id;
    const openDetails = new Map([...root.querySelectorAll('[data-bs-refresh-block]')].map(block => [block.id, block.querySelector('details')?.open]));
    freezeBuy(root);
    root.querySelector('[data-bs-update-error]').hidden = true;
    root.querySelector('[data-bs-variant-status]').textContent = root.dataset.loadingText;
    const requestURL = new URL(url);
    requestURL.searchParams.set('section_id', root.dataset.sectionId);
    try {
      const response = await fetch(requestURL.href, {signal: controller.signal, headers: {'X-Requested-With': 'XMLHttpRequest'}, credentials: 'same-origin'});
      if (!response.ok) throw new Error('Section unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextRoot = html.getElementById(root.id);
      if (!nextRoot?.matches('[data-bs-product]') || nextRoot.dataset.productId !== root.dataset.productId) throw new Error('Unexpected product section');
      if (controller.signal.aborted || !root.isConnected) return;
      // DOMParser parses noscript styles as real nodes. Do not activate the
      // no-JavaScript fallback when adopting sections into a scripted page.
      nextRoot.querySelectorAll('noscript').forEach(node => node.remove());
      preserveQuantity(nextRoot, oldQuantity);
      const nextGallery = nextRoot.querySelector('[data-bs-product-gallery]');
      const oldGallery = root.querySelector('[data-bs-product-gallery]');
      if (nextGallery && oldGallery) {
        oldGallery.querySelectorAll('[data-bs-media-panel]').forEach(pauseMedia);
        oldGallery.replaceWith(nextGallery);
      }
      root.querySelectorAll('[data-bs-refresh-block]').forEach(block => {
        const replacement = nextRoot.querySelector('#' + CSS.escape(block.id));
        if (!replacement) return;
        const details = replacement.querySelector('details');
        if (details && typeof openDetails.get(block.id) === 'boolean') details.open = openDetails.get(block.id);
        // EasySell binds directly to its custom trigger. Keep that node and its
        // listener, but move it into the freshly rendered variant form.
        const codButton = block.querySelector('[data-bs-cod-button]');
        const nextCodButton = replacement.querySelector('[data-bs-cod-button]');
        if (codButton && nextCodButton) {
          codButton.disabled = nextCodButton.disabled;
          nextCodButton.replaceWith(codButton);
        }
        block.replaceWith(replacement);
      });
      root.dataset.variantId = nextRoot.dataset.variantId || '';
      root.dataset.updating = 'false';
      root.removeAttribute('aria-busy');
      root.querySelector('[data-bs-variant-status]').textContent = root.dataset.updatedText;
      const visibleURL = new URL(url);
      if (root.dataset.variantId) {
        visibleURL.searchParams.delete('option_values');
        visibleURL.searchParams.set('variant', root.dataset.variantId);
      }
      history.replaceState(history.state, '', visibleURL.href);
      document.getElementById(focusedId)?.focus({preventScroll: true});
      window.Shopify?.PaymentButton?.init();
      root.dispatchEvent(new CustomEvent('bs:product:updated', {bubbles: true, detail: {sectionId: root.dataset.sectionId, productId: root.dataset.productId, variantId: root.dataset.variantId}}));
    } catch (error) {
      if (error.name === 'AbortError' || !root.isConnected) return;
      root.removeAttribute('aria-busy');
      const message = root.querySelector('[data-bs-update-error]');
      message.textContent = root.dataset.errorText;
      message.hidden = false;
      root.querySelector('[data-bs-variant-status]').textContent = '';
    }
  }
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-bs-option-value]');
    if (input) updateVariant(queryRoot(input), input);
  });
  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-bs-product-form]');
    if (form && queryRoot(form)?.dataset.updating === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('click', event => {
    const thumb = event.target.closest('[data-bs-media-target]');
    if (thumb) { selectMedia(queryRoot(thumb), thumb.dataset.bsMediaTarget); return; }
    const media = event.target.closest('[data-bs-load-media]');
    if (media) { loadMedia(media); return; }
    const zoom = event.target.closest('[data-bs-zoom-src]');
    if (zoom) {
      const dialog = queryRoot(zoom).querySelector('[data-bs-product-zoom]');
      if (!dialog) return;
      const image = dialog.querySelector('[data-bs-zoom-image]');
      image.src = zoom.dataset.bsZoomSrc;
      image.alt = zoom.dataset.bsZoomAlt || '';
      dialog.showModal();
      return;
    }
    if (event.target.matches('[data-bs-product-zoom]')) {
      const bounds = event.target.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) event.target.close();
    }
  });
  document.addEventListener('keydown', event => {
    const thumb = event.target.closest('[data-bs-media-target]');
    if (!thumb || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const siblings = [...queryRoot(thumb).querySelectorAll('[data-bs-media-target]')];
    let position = siblings.indexOf(thumb);
    if (event.key === 'Home') position = 0;
    else if (event.key === 'End') position = siblings.length - 1;
    else position = (position + (event.key === 'ArrowRight' ? 1 : -1) + siblings.length) % siblings.length;
    siblings[position].focus();
    selectMedia(queryRoot(thumb), siblings[position].dataset.bsMediaTarget);
  });
  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.querySelector('[data-bs-product]');
    if (root) {
      requests.get(root)?.abort();
      root.querySelectorAll('[data-bs-media-panel]').forEach(pauseMedia);
    }
  });
})();
